import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, FormField, Input, Modal } from '@/components/ui';
import styles from './accountDialogs.module.css';

const CONFIRM_PHRASE = 'XOA';

interface Props {
    open: boolean;
    count: number;
    pending: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

/**
 * EAA-1820 (frame 34): destructive confirm for ≥2 selected accounts — names
 * the blast radius and requires typing "XOA" before the destructive button
 * enables. A single-row delete keeps using the existing `ConfirmButton`
 * (native Popconfirm) already wired in `AccountsPage.tsx`; this typed
 * variant is reserved for the multi-row case the ticket calls out
 * specifically ("≥ 2 items require typing XOA").
 *
 * EAA-1804: footer swapped to the kit `Button` instead of hand-written
 * `<button className="portal-btn ...">`; the "type XOA" field's
 * `<label><span className="portal-field-label">…` chrome swapped to kit
 * `FormField` + `Input`.
 */
export function BulkDeleteConfirmModal({ open, count, pending, onCancel, onConfirm }: Props) {
    const { t } = useTranslation();
    const [typed, setTyped] = useState('');

    const close = () => {
        setTyped('');
        onCancel();
    };

    const confirmed = typed.trim().toUpperCase() === CONFIRM_PHRASE;

    return (
        // EAA-1804: root debugging handle, matching the folder's existing acc-* prefix.
        <Modal
            className="acc-bulk-delete-modal"
            open={open}
            onCancel={close}
            title={t('console.accounts.bulkDeleteModal.title', { count })}
            footer={
                <>
                    <Button onClick={close} disabled={pending}>
                        {t('common.cancel')}
                    </Button>
                    <Button danger disabled={!confirmed || pending} onClick={onConfirm}>
                        {pending
                            ? t('console.accounts.bulkDeleteModal.pending')
                            : t('console.accounts.bulkDeleteModal.submit', { count })}
                    </Button>
                </>
            }
        >
            <div className="portal-admin-form" style={{ gridTemplateColumns: '1fr' }}>
                <p className="portal-admin-hint">
                    {t('console.accounts.bulkDeleteModal.hint', { count })}
                </p>

                {/* EAA-1820 (frame 68): the two consequences the frame requires
                    be stated. Neither is cosmetic — the audit NOT cascading is
                    what makes the delete traceable afterwards, and the
                    assignments NOT cascading is a real cleanup the operator owns
                    and would otherwise discover later as orphaned rows. */}
                <ul className={styles.deleteConsequences}>
                    <li>
                        <strong className={styles.deleteTerm}>
                            {t('console.accounts.bulkDeleteModal.auditLogTerm')}
                        </strong>{' '}
                        {t('console.accounts.bulkDeleteModal.auditLogNote')}
                    </li>
                    <li>
                        <strong className={styles.deleteTerm}>
                            {t('console.accounts.bulkDeleteModal.assignmentsTerm')}
                        </strong>{' '}
                        {t('console.accounts.bulkDeleteModal.assignmentsNote')}
                    </li>
                </ul>
                <FormField
                    label={
                        <>
                            {t('console.accounts.bulkDeleteModal.confirmLabelPrefix')} <code>XOA</code>{' '}
                            {t('console.accounts.bulkDeleteModal.confirmLabelSuffix')}
                        </>
                    }
                >
                    <Input
                        value={typed}
                        onChange={(e) => setTyped(e.target.value)}
                        placeholder="XOA"
                        autoFocus
                    />
                </FormField>
            </div>
        </Modal>
    );
}
